import { SfxToggle, useSfx } from '@slingu/ui';
import { useEffect, useMemo, useRef, useState } from 'react';

import { commitScore, getHighScore } from './highscores.js';
import { LEGACY_POOL } from './vocabulary.js';

const HIGH_SCORE_ID = 'hangman';

export type HangmanProps = {
  onExit: (result: { score: number; wins: number; losses: number; xp: number }) => void;
};

type Phase = 'menu' | 'playing' | 'over';

const MAX_MISSES = 6; // parts of the hangman drawing
const WORD_COUNT = 8;
const XP_WIN = 18;

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

/**
 * Hangman — o clássico da forca. Dado um prompt em PT, o jogador descobre
 * a palavra em inglês tocando nas letras. 6 erros = enforca. 8 palavras
 * por jogo.
 */
export function Hangman({ onExit }: HangmanProps) {
  const sfx = useSfx();
  const sfxRef = useRef(sfx);
  sfxRef.current = sfx;

  const [phase, setPhase] = useState<Phase>('menu');
  const [queue, setQueue] = useState<{ pt: string; en: string }[]>([]);
  const [roundIdx, setRoundIdx] = useState(0);
  const [misses, setMisses] = useState(0);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [wins, setWins] = useState(0);
  const [losses, setLosses] = useState(0);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState<number>(() => getHighScore(HIGH_SCORE_ID));
  const [newRecord, setNewRecord] = useState<boolean>(false);
  const [roundStatus, setRoundStatus] = useState<null | 'won' | 'lost'>(null);

  const current = queue[roundIdx];
  const target = (current?.en ?? '').toUpperCase();
  const display = useMemo(() => {
    return target
      .split('')
      .map((ch) => (ch === ' ' ? ' ' : picked.has(ch) ? ch : '_'))
      .join('');
  }, [target, picked]);

  function shuffle<T>(arr: T[]): T[] {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j]!, a[i]!];
    }
    return a;
  }

  function startGame() {
    // Pick WORD_COUNT words with length <= 10 for tractable hangman.
    const candidates = LEGACY_POOL.filter(
      (p) => p.correct.length <= 10 && !p.correct.includes('-') && !/\s/.test(p.correct),
    );
    const picked = shuffle(candidates)
      .slice(0, WORD_COUNT)
      .map((p) => ({ pt: p.promptPt ?? p.correct, en: p.correct }));
    setQueue(picked);
    setRoundIdx(0);
    setMisses(0);
    setPicked(new Set());
    setWins(0);
    setLosses(0);
    setScore(0);
    setRoundStatus(null);
    setPhase('playing');
    sfxRef.current.play('start');
  }

  function pickLetter(letter: string) {
    if (roundStatus) return;
    if (picked.has(letter)) return;
    sfxRef.current.play('tap');
    const next = new Set(picked);
    next.add(letter);
    setPicked(next);
    if (target.includes(letter)) {
      sfxRef.current.play('xp-gain');
      // Win check
      const remaining = target.split('').filter((ch) => ch !== ' ' && !next.has(ch));
      if (remaining.length === 0) {
        setRoundStatus('won');
        setWins((w) => w + 1);
        const bonus = Math.max(0, MAX_MISSES - misses) * 3;
        setScore((s) => s + XP_WIN + bonus);
        sfxRef.current.play('level-up');
        window.setTimeout(nextRound, 1400);
      }
    } else {
      sfxRef.current.play('heart-loss');
      const newMisses = misses + 1;
      setMisses(newMisses);
      if (newMisses >= MAX_MISSES) {
        setRoundStatus('lost');
        setLosses((l) => l + 1);
        sfxRef.current.play('game-over');
        window.setTimeout(nextRound, 1800);
      }
    }
  }

  function nextRound() {
    const next = roundIdx + 1;
    if (next >= queue.length) {
      const result = commitScore(HIGH_SCORE_ID, score);
      setHighScore(result.best);
      setNewRecord(result.isNew);
      setPhase('over');
      return;
    }
    setRoundIdx(next);
    setMisses(0);
    setPicked(new Set());
    setRoundStatus(null);
  }

  // Keyboard support
  useEffect(() => {
    if (phase !== 'playing') return;
    function onKey(e: KeyboardEvent) {
      const ch = e.key.toUpperCase();
      if (ch.length === 1 && /[A-Z]/.test(ch)) pickLetter(ch);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, picked, roundStatus, target, misses]);

  return (
    <div className="sl-minigame sl-minigame--portrait">
      <button
        type="button"
        className="sl-minigame__close"
        onClick={() => onExit({ score, wins, losses, xp: score })}
        aria-label="Fechar jogo"
      >
        ×
      </button>
      <div className="sl-flappy__sfx"><SfxToggle variant="icon" /></div>

      {phase === 'menu' ? (
        <div className="sl-flappy__menu">
          <h2><span aria-hidden>🪢</span> Hangman</h2>
          <p>
            Dica em português, descubra a palavra em inglês letra por letra.{' '}
            <strong>6 erros enforca o Slingu</strong>. 8 palavras por jogo,
            sorteadas de um pool de 1000+ palavras.
          </p>
          {highScore > 0 ? (
            <p className="sl-flappy__record">
              🏆 Seu recorde: <strong>{highScore} XP</strong>
            </p>
          ) : null}
          <div className="sl-flappy__menu-actions">
            <button
              type="button"
              className="sl-btn sl-btn--primary sl-btn--lg sl-btn--upper"
              onClick={() => { setNewRecord(false); startGame(); }}
            >Começar</button>
          </div>
        </div>
      ) : null}

      {phase === 'playing' && current ? (
        <div className="sl-hangman">
          <div className="sl-wbuilder__hud">
            <span>{roundIdx + 1}/{queue.length}</span>
            <span>♥ {MAX_MISSES - misses}</span>
            <span className="sl-wbuilder__hud-item--xp">{score} XP</span>
          </div>

          <Gallows misses={misses} />

          <div className="sl-hangman__hint">
            <span className="sl-wbuilder__prompt-eyebrow">TRADUZA</span>
            <p className="sl-hangman__hint-text">{current.pt}</p>
          </div>

          <div className="sl-hangman__word" role="status" aria-label={`Palavra: ${display}`}>
            {display.split('').map((ch, i) => (
              <span key={i} className={`sl-hangman__slot${ch === '_' ? ' sl-hangman__slot--empty' : ''}${ch === ' ' ? ' sl-hangman__slot--space' : ''}`}>
                {ch === '_' ? '' : ch}
              </span>
            ))}
          </div>

          {roundStatus === 'won' ? (
            <p className="sl-wbuilder__feedback sl-wbuilder__feedback--correct">Salvou o Slingu!</p>
          ) : roundStatus === 'lost' ? (
            <p className="sl-wbuilder__feedback sl-wbuilder__feedback--wrong">
              Era <strong>{target}</strong>.
            </p>
          ) : null}

          <div className="sl-hangman__keys">
            {ALPHABET.map((letter) => {
              const used = picked.has(letter);
              const inWord = target.includes(letter);
              const cls = used
                ? inWord
                  ? 'sl-hangman__key sl-hangman__key--hit'
                  : 'sl-hangman__key sl-hangman__key--miss'
                : 'sl-hangman__key';
              return (
                <button
                  key={letter}
                  type="button"
                  className={cls}
                  onClick={() => pickLetter(letter)}
                  disabled={used || !!roundStatus}
                >
                  {letter}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {phase === 'over' ? (
        <div className="sl-flappy__over">
          <h2>{newRecord ? '🏆 Novo recorde!' : wins > losses ? 'Salvou o Slingu!' : 'Rude.'}</h2>
          <p>
            Vitórias: <strong>{wins}</strong> · Derrotas: <strong>{losses}</strong><br />
            XP: <strong>{score}</strong>
          </p>
          <p className="sl-flappy__record">
            🏆 Recorde: <strong>{highScore} XP</strong>
            {newRecord ? ' ✨' : null}
          </p>
          <button
            type="button"
            className="sl-btn sl-btn--primary sl-btn--lg sl-btn--upper"
            onClick={() => onExit({ score, wins, losses, xp: score })}
          >Voltar pro app</button>
        </div>
      ) : null}
    </div>
  );
}

function Gallows({ misses }: { misses: number }) {
  // Step by step reveal: head, body, left arm, right arm, left leg, right leg
  return (
    <svg viewBox="0 0 200 200" className="sl-hangman__gallows" aria-hidden>
      {/* Base structure always visible */}
      <line x1="20" y1="190" x2="180" y2="190" stroke="currentColor" strokeWidth="4" />
      <line x1="50" y1="190" x2="50" y2="20" stroke="currentColor" strokeWidth="4" />
      <line x1="50" y1="20" x2="130" y2="20" stroke="currentColor" strokeWidth="4" />
      <line x1="130" y1="20" x2="130" y2="40" stroke="currentColor" strokeWidth="3" />
      {/* 1: Head */}
      {misses >= 1 && <circle cx="130" cy="54" r="14" fill="none" stroke="currentColor" strokeWidth="3" />}
      {/* 2: Body */}
      {misses >= 2 && <line x1="130" y1="68" x2="130" y2="120" stroke="currentColor" strokeWidth="3" />}
      {/* 3: Left arm */}
      {misses >= 3 && <line x1="130" y1="80" x2="110" y2="100" stroke="currentColor" strokeWidth="3" />}
      {/* 4: Right arm */}
      {misses >= 4 && <line x1="130" y1="80" x2="150" y2="100" stroke="currentColor" strokeWidth="3" />}
      {/* 5: Left leg */}
      {misses >= 5 && <line x1="130" y1="120" x2="112" y2="148" stroke="currentColor" strokeWidth="3" />}
      {/* 6: Right leg */}
      {misses >= 6 && <line x1="130" y1="120" x2="148" y2="148" stroke="currentColor" strokeWidth="3" />}
    </svg>
  );
}
